import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { LoadingSpinner } from './LoadingSpinner';

export interface TemplatePreviewProps {
	// HTML content to preview
	content?: string;

	// Loading state
	loading?: boolean;

	// Error state
	error?: string;

	// Empty state message
	emptyMessage?: string;

	// Additional CSS classes
	className?: string;

	// Max height for scrollable area
	maxHeight?: string;

	// Show border
	bordered?: boolean;

	// Padding size
	padding?: 'none' | 'sm' | 'md' | 'lg';

	// Render the content inside a sandboxed iframe instead of inline.
	// Use for full HTML emails (e.g. compiled MJML) whose <style> blocks and
	// table layout would otherwise leak into / be clobbered by the app's styles.
	isolate?: boolean;
}

export function TemplatePreview({
	content,
	loading = false,
	error,
	emptyMessage = 'No preview available',
	className,
	maxHeight = '400px',
	bordered = true,
	padding = 'md',
	isolate = false,
}: TemplatePreviewProps) {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const paddingClasses = {
		none: '',
		sm: 'p-2',
		md: 'p-4',
		lg: 'p-6',
	}[padding];

	const containerClasses = cn(
		'overflow-auto',
		bordered && `border ${colors.classes.borderPrimary} rounded-lg`,
		colors.classes.bgPrimary,
		paddingClasses,
		className,
	);

	if (loading) {
		return (
			<div className={containerClasses} style={{ maxHeight }}>
				<div className="flex items-center justify-center py-12">
					<LoadingSpinner />
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={containerClasses} style={{ maxHeight }}>
				<div className={`text-center py-8 ${colors.classes.textError}`}>
					<p className="text-sm">{t(error)}</p>
				</div>
			</div>
		);
	}

	if (!content) {
		return (
			<div className={containerClasses} style={{ maxHeight }}>
				<div className={`text-center py-8 ${colors.classes.textSecondary}`}>
					<p className="text-sm">{t(emptyMessage)}</p>
				</div>
			</div>
		);
	}

	if (isolate) {
		return (
			<div className={containerClasses} style={{ maxHeight }}>
				<iframe
					title={t('Email preview')}
					srcDoc={content}
					sandbox=""
					className="w-full border-0"
					style={{ minHeight: maxHeight }}
				/>
			</div>
		);
	}

	return (
		<div className={containerClasses} style={{ maxHeight }}>
			<div
				className={`prose prose-sm dark:prose-invert max-w-none email-preview ${colors.classes.textPrimary}`}
				dangerouslySetInnerHTML={{ __html: content }}
			/>
		</div>
	);
}
